/** Client MCP **Limit hits** (client MCP plan §7.6 "How we tune"; backend WPS P0-07 + W3-FIX-B):
 *  which limits clients reach, how often and by how many callers — one row per UTC day, tenant and
 *  limit, so ops can see which limits bite and raise them (a settings change).
 *
 *  `GET /mcp_limit_hits/` (`console_mcp_limits_view`, Ops Admin by default):
 *  - `since` / `until` — UTC days, both included. With `tour_operator_id`: by default the last 7
 *    days, at most 31. Without it (every tenant): by default the last 3 days, and **at most 3** —
 *    a longer window is a 400 (a month of every tenant's access log is too much to scan). The
 *    answer names the window it used.
 *  - `limit` — a limit key or a comma-separated list (`limits` in the answer lists them all).
 *  - Rate limits are counted from the access log (MCP rows `throttled` with
 *    `limit_reached:<key>`), spend ceilings and caps from their audit event `mcp_limit_reached`;
 *    each hit once. Newest day first, then the most hits; at most 1000 rows (`truncated`).
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { apiGet, extendedListConfig } from './client';
import type { components } from './types';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type McpLimitHits = Schemas['McpLimitHits'];
export type McpLimitHitRow = Schemas['McpLimitHitRow'];
export type McpLimit = Schemas['McpLimit'];

/** The longest window, in days (today included), with and without a tenant filter. */
export const MAX_DAYS_WITH_TENANT = 31;
export const MAX_DAYS_ALL_TENANTS = 3;

export interface McpLimitHitsParams {
  /** First UTC day, `YYYY-MM-DD`. */
  since?: string;
  /** Last UTC day (included), `YYYY-MM-DD`. */
  until?: string;
  tour_operator_id?: TenantIdsParam;
  /** A limit key or a comma-separated list. */
  limit?: string;
  timeout?: 'extended';
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function limitHits(params: McpLimitHitsParams = {}): Promise<McpLimitHits> {
  return apiGet(`/mcp_limit_hits/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

export const mcpLimitHitKeys = {
  all: ['mcpLimitHits'] as const,
  hits: (params: McpLimitHitsParams) => ['mcpLimitHits', params] as const,
};

export const LIMIT_KIND_LABELS: Record<McpLimit['kind'], string> = {
  rate: 'Rate limit',
  spend: 'Spend ceiling',
  cap: 'Cap',
};

/** Whose allowance it is, as the client's `limit_reached` error names it. */
export const LIMIT_SCOPE_LABELS: Record<McpLimit['scope'], string> = {
  you: 'One caller',
  your_company: 'The whole tenant',
  this_run: 'One run',
};

export const LIMIT_SOURCE_LABELS: Record<McpLimitHitRow['source'], string> = {
  access_log: 'Access log',
  audit: 'Audit event',
};

/** `YYYY-MM-DD` → its whole-day count from `since` to `until`, both included (UTC). */
export function windowDays(since: string, until: string): number | null {
  const a = Date.parse(`${since}T00:00:00Z`);
  const b = Date.parse(`${until}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000) + 1;
}
